import { Alert, Box, Grid2 as Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetList } from "react-admin";
import { PolicyDashboardFilters } from "@/pages/dashboards/policyDashboard/PolicyDashboardFilters";
import { DashboardCard } from "@/components/common/DashboardCard";
import { SpecDeductibleCompositePremium } from "@/components/charts/policyCharts/SpecDeductibleCompositePremium";
import { SpecDeductibleChart } from "@/components/charts/policyCharts/SpecDeductibleChart";
import { LaseredClaimantsChart } from "@/components/charts/policyCharts/LaseredClaimantsChart";
import { LaseredImpactChart } from "@/components/charts/policyCharts/LaseredImpactChart";
import { RateCapChart } from "@/components/charts/policyCharts/RateCapChart";
import { RateCapPercentageChart } from "@/components/charts/policyCharts/RateCapPercentageChart";
import { AggregateMonthlyFactorScatterChart } from "@/components/charts/policyCharts/AggregateMonthlyFactorScatterChart";
import { CarrierDistributionChart } from "@/components/charts/policyCharts/CarrierDistributionChart";
import { GroupSizeDistributionChart } from "@/components/charts/policyCharts/GroupSizeDistributionChart";
import { TpaDistributionChart } from "@/components/charts/policyCharts/TpaDistributionChart";
import { RxDistributionChart } from "@/components/charts/policyCharts/RxDistributionChart";
import { TpaCarrierPairingsChart } from "@/components/charts/policyCharts/TpaCarrierPairingsChart";
import { VendorRelationshipChart } from "@/components/charts/policyCharts/VendorRelationshipChart";
import { RenewalTimelineChart } from "@/components/charts/policyCharts/RenewalTimelineChart";
import { ClientSizeDeductibleChart } from "@/components/charts/policyCharts/ClientSizeDeductibleChart";
import { type Policy, ResourceName } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { PageHeader } from "@/components/common/PageHeader";
import { EndorsementsStackedChart } from "@/components/charts/policyCharts/EndorsementsStackedChart";
import { AggMonthlyFactorCompositePremium } from "@/components/charts/policyCharts/AggMonthlyFactorCompositePremium";
import { BreachRateByCarrierChart } from "@/components/charts/policyCharts/BreachRateByCarrierChart";
import { AccommodationFundingTrendChart } from "@/components/charts/policyCharts/AccommodationFundingTrendChart";
import { SpecDeductibleEnrollmentBarChart } from "@/components/charts/policyCharts/SpecDeductibleEnrollmentBarChart";
import { PageContent, TabContent } from "@/components/common/PageLayout";
import { getResourceIcon } from "@/utils/icons";
import {
  Shield as ShieldIcon,
  TrendingUp as TrendingUpIcon,
  ShowChart as ShowChartIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import { MetricCardsGrid, MetricCard } from "@/components/common/MetricCards";
import { StyledTab, StyledTabs } from "@/components/ui/mui/StyledTabs";
import { usePolicyDashboardData } from "./usePolicyDashboardData";
import { buildPolicyDrillLink } from "./buildPolicyDrillLink";

/** Tab definition with label and a data-presence check. */
interface DashboardTab {
  label: string;
  hasData: boolean;
}

export const PolicyDashboard = () => {
  const [searchParams] = useSearchParams();
  const PolicyIcon = getResourceIcon(ResourceName.POLICIES);
  const [activeTab, setActiveTab] = useState(0);

  const rawFilter = searchParams.get("filter");
  // Memoize on rawFilter so `filters` keeps a stable identity across renders —
  // otherwise the drill-link useMemos (and the analytics query key) recompute
  // every render.
  const filters = useMemo<Record<string, string | string[]>>(() => {
    try {
      return rawFilter ? JSON.parse(rawFilter) : {};
    } catch {
      return {};
    }
  }, [rawFilter]);

  // Fetch all policies (lightweight, for the filter dropdowns only).
  // NOTE: Capped at 1000 policies for filter population. If partnerships exceed
  // this, consider deriving filter options from the analytics endpoint instead.
  const { data: allPolicies = [] } = useGetList<Policy>(ResourceName.POLICIES, {
    pagination: { page: 1, perPage: 1000 },
  });

  // Fetch pre-aggregated analytics from the single backend endpoint
  const {
    data: analyticsData,
    isLoading,
    isError,
    error,
  } = usePolicyDashboardData({
    vendor_id_ref: filters.vendor_id_ref,
    client_id_ref: filters.client_id_ref,
    tpa_vendor_id: filters.tpa_vendor_ids,
    rx_vendor_id: filters.rx_vendor_ids,
  });

  const metrics = analyticsData?.metrics;
  const specificScatter = analyticsData?.specific_benefit?.deductible_vs_premium ?? [];
  const enrollmentBuckets = analyticsData?.specific_benefit?.enrollment_buckets ?? [];
  const aggregateScatter = analyticsData?.aggregate_benefit?.monthly_factor_vs_premium ?? [];
  const breachSummary = analyticsData?.aggregate_benefit?.breach_summary;
  const breachRateByCarrier = analyticsData?.aggregate_benefit?.breach_rate_by_carrier ?? [];
  const accommodationFundingTrend = analyticsData?.aggregate_benefit?.accommodation_funding_trend ?? [];
  const endorsementCounts = analyticsData?.endorsements?.endorsement_type_counts ?? [];
  const laseredData = analyticsData?.endorsements?.lasered_data ?? [];
  const rateCapData = analyticsData?.endorsements?.rate_cap_data ?? [];
  const tpaDistribution = analyticsData?.vendors?.tpa_distribution ?? [];
  const rxDistribution = analyticsData?.vendors?.rx_distribution ?? [];
  const tpaCarrierPairings = analyticsData?.vendors?.tpa_carrier_pairings ?? [];
  const carrierVendorSummary = analyticsData?.vendors?.carrier_vendor_summary ?? [];
  const renewalTimeline = analyticsData?.renewal_timeline ?? [];

  const hasRateCaps = rateCapData.length > 0;
  const hasLasered = laseredData.length > 0;
  const hasEndorsementCounts = endorsementCounts.length > 0;
  const hasTpaData = tpaDistribution.length > 0;
  const hasRxData = rxDistribution.length > 0;
  const hasRenewalData = renewalTimeline.length > 0;
  const hasBreachData =
    breachRateByCarrier.length > 0 ||
    (breachSummary?.policies_with_breach_count ?? 0) > 0 ||
    accommodationFundingTrend.length > 0;

  const rateCapCount = useMemo(() => {
    const entry = endorsementCounts.find((e) => e.endorsement_type === "rate_caps");
    return entry?.policy_count ?? 0;
  }, [endorsementCounts]);

  const totalActivePolicies = metrics?.active_policy_count ?? 0;

  // Top-card drill-throughs: each card links to the policy list showing exactly
  // the population its metric was computed over — active policies, carrying the
  // dashboard's current Client/Carrier/TPA/Rx filters. The average cards add a
  // benefit-presence predicate so they land on the specific/aggregate subset.
  const activePoliciesLink = useMemo(() => buildPolicyDrillLink(filters), [filters]);
  const specificBenefitLink = useMemo(() => buildPolicyDrillLink(filters, { has_specific_benefit: true }), [filters]);
  const aggregateBenefitLink = useMemo(() => buildPolicyDrillLink(filters, { has_aggregate_benefit: true }), [filters]);

  // Determine which tabs have data (hide empty tabs once data has loaded)
  const tabs: DashboardTab[] = useMemo(
    () => [
      {
        label: "Overview",
        hasData: specificScatter.length > 0 || hasTpaData || hasRxData || hasRenewalData,
      },
      { label: "Specific Benefit", hasData: specificScatter.length > 0 || enrollmentBuckets.length > 0 },
      { label: "Aggregate Benefit", hasData: aggregateScatter.length > 0 || hasBreachData },
      { label: "Endorsements", hasData: hasLasered || hasRateCaps || hasEndorsementCounts },
    ],
    [
      specificScatter,
      enrollmentBuckets,
      aggregateScatter,
      hasLasered,
      hasRateCaps,
      hasEndorsementCounts,
      hasTpaData,
      hasRxData,
      hasRenewalData,
      hasBreachData,
    ]
  );

  // While loading, show all tabs. Once loaded, filter to those with data.
  const visibleTabs = isLoading ? tabs : tabs.filter((t) => t.hasData);

  // Reset to first tab if current tab is no longer visible
  useEffect(() => {
    if (!isLoading && activeTab >= visibleTabs.length && visibleTabs.length > 0) {
      setActiveTab(0);
    }
  }, [isLoading, activeTab, visibleTabs.length]);

  // Map visible tab index → original tab index for rendering
  const activeOriginalIndex = visibleTabs[activeTab]
    ? tabs.findIndex((t) => t.label === visibleTabs[activeTab].label)
    : 0;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <PageHeader
        customTitle="Policy Dashboard"
        customSubtitle="Comprehensive view of policy data, from deductibles to endorsements."
        hideSectionTitle
        headerIcon={<PolicyIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />

      <PageContent>
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to load dashboard data. {error instanceof Error ? error.message : "Please try again."}
          </Alert>
        )}

        {!isLoading && !isError && totalActivePolicies === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 12,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            <PolicyIcon sx={{ fontSize: 48, mb: 2, opacity: 0.4 }} />
            <Typography variant="h6" fontWeight={500}>
              No active policies
            </Typography>
            <Typography variant="body2">
              Policy data will appear here once policies are created for this partnership.
            </Typography>
          </Box>
        )}

        {(isLoading || totalActivePolicies > 0) && (
          <>
            <Box sx={{ pb: 4 }}>
              <MetricCardsGrid columns={5}>
                <MetricCard
                  icon={<PolicyIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Active Policies"
                  value={metrics?.active_policy_count ?? 0}
                  link={activePoliciesLink}
                  hint="View these policies in a filtered list"
                  isLoading={isLoading}
                />
                <MetricCard
                  icon={<ShieldIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Avg Specific Deductible"
                  value={formatUSD(metrics?.avg_specific_deductible ?? 0)}
                  link={specificBenefitLink}
                  hint="View the policies with a specific benefit behind this average"
                  isLoading={isLoading}
                />
                <MetricCard
                  icon={<TrendingUpIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Avg Specific PEPM"
                  value={formatUSD(metrics?.avg_specific_composite_premium ?? 0)}
                  link={specificBenefitLink}
                  hint="View the policies with a specific benefit behind this average"
                  isLoading={isLoading}
                />
                <MetricCard
                  icon={<ShowChartIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Avg Aggregate Monthly Factor"
                  value={formatUSD(metrics?.avg_aggregate_monthly_factor ?? 0)}
                  link={aggregateBenefitLink}
                  hint="View the policies with an aggregate benefit behind this average"
                  isLoading={isLoading}
                />
                <MetricCard
                  icon={<AccountBalanceIcon sx={{ fontSize: 18, color: "primary.main" }} />}
                  title="Avg Aggregate PEPM"
                  value={formatUSD(metrics?.avg_aggregate_composite_premium ?? 0)}
                  link={aggregateBenefitLink}
                  hint="View the policies with an aggregate benefit behind this average"
                  isLoading={isLoading}
                />
              </MetricCardsGrid>
            </Box>

            {/* Tabs and Filters Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <StyledTabs value={activeTab} onChange={handleTabChange}>
                  {visibleTabs.map((tab) => (
                    <StyledTab key={tab.label} label={tab.label} />
                  ))}
                </StyledTabs>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <PolicyDashboardFilters allPolicies={allPolicies} filters={filters} />
              </Box>
            </Box>

            {/* Tab 0: Overview — donuts, renewal, vendor relationships */}
            {activeOriginalIndex === 0 && (
              <TabContent>
                <Grid container spacing={1.5}>
                  <Grid size={4}>
                    <DashboardCard title="Carrier Distribution" description="Policies by stop-loss carrier.">
                      <CarrierDistributionChart data={specificScatter} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={4}>
                    <DashboardCard title="TPA Distribution" description="Policies by TPA.">
                      <TpaDistributionChart data={tpaDistribution} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={4}>
                    <DashboardCard title="Rx/PBM Distribution" description="Policies by pharmacy benefit manager.">
                      <RxDistributionChart data={rxDistribution} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard title="Renewal Timeline" description="Policy effective start dates by month.">
                      <RenewalTimelineChart data={renewalTimeline} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard title="Group Size Distribution" description="Policies by enrollment size.">
                      <GroupSizeDistributionChart data={specificScatter} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard title="TPA × Carrier Pairings" description="Top 5 carriers per TPA.">
                      <TpaCarrierPairingsChart data={tpaCarrierPairings} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard title="Vendor Relationships" description="Carrier book by TPA composition.">
                      <VendorRelationshipChart data={carrierVendorSummary} />
                    </DashboardCard>
                  </Grid>
                </Grid>
              </TabContent>
            )}

            {/* Tab 1: Specific Benefit */}
            {activeOriginalIndex === 1 && (
              <TabContent>
                <Grid container spacing={1.5}>
                  <Grid size={6}>
                    <DashboardCard
                      title="Enrollment by Deductible"
                      description="Enrolled members by $50k deductible range, by carrier."
                    >
                      <SpecDeductibleEnrollmentBarChart data={enrollmentBuckets} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Deductible vs Premium"
                      description="Each dot is a policy — deductible vs composite premium."
                    >
                      <SpecDeductibleCompositePremium data={specificScatter} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Deductible by Group Size"
                      description="Specific deductible vs enrollment, colored by carrier."
                    >
                      <SpecDeductibleChart data={specificScatter} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Client Size vs Deductible"
                      description="Larger clients tend to have higher deductibles — spot the outliers."
                    >
                      <ClientSizeDeductibleChart data={specificScatter} />
                    </DashboardCard>
                  </Grid>
                </Grid>
              </TabContent>
            )}

            {/* Tab 2: Aggregate Benefit */}
            {activeOriginalIndex === 2 && (
              <TabContent>
                <Grid container spacing={1.5}>
                  <Grid size={6}>
                    <DashboardCard title="Monthly Factor by Carrier" description="Aggregate monthly factor by carrier.">
                      <AggregateMonthlyFactorScatterChart data={aggregateScatter} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Monthly Factor vs Premium"
                      description="Each dot is a policy — monthly factor vs aggregate premium."
                    >
                      <AggMonthlyFactorCompositePremium data={aggregateScatter} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard title="Breach Rate by Carrier" description="Breached vs total policies per carrier.">
                      <BreachRateByCarrierChart data={breachRateByCarrier} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard
                      title="Accommodation Funding Trend"
                      description="Monthly accommodation funded over time."
                    >
                      <AccommodationFundingTrendChart data={accommodationFundingTrend} />
                    </DashboardCard>
                  </Grid>
                </Grid>
              </TabContent>
            )}

            {/* Tab 3: Endorsements */}
            {activeOriginalIndex === 3 && (
              <TabContent>
                <Grid container spacing={1.5}>
                  <Grid size={6}>
                    <DashboardCard title="Lasered Claimants" description="Lasered vs non-lasered by carrier.">
                      <LaseredClaimantsChart data={laseredData} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard title="Lasered Impact" description="Avg lasered deductible and count per client.">
                      <LaseredImpactChart data={laseredData} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard title="Rate Cap Endorsement" description="Policies with and without rate caps.">
                      <RateCapChart rateCapCount={rateCapCount} totalActivePolicies={totalActivePolicies} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCard title="Rate Cap Percentage" description="Distribution of rate cap percentages.">
                      <RateCapPercentageChart data={rateCapData} />
                    </DashboardCard>
                  </Grid>
                  <Grid size={12}>
                    <DashboardCard title="Endorsement Adoption" description="Which endorsements are most common.">
                      <EndorsementsStackedChart data={endorsementCounts} totalActivePolicies={totalActivePolicies} />
                    </DashboardCard>
                  </Grid>
                </Grid>
              </TabContent>
            )}
          </>
        )}
      </PageContent>
    </>
  );
};
